import { ArrowDropDown } from "@mui/icons-material";
import { Box, FormLabel, Link, MenuItem, Stack, TextField, Tooltip, useTheme } from "@mui/material";
import InfoIcon from "atoms/InfoIcon";
import SimpleMenu from "atoms/SimpleMenu";
import { ExtendedDataProvider } from "misc/dataProvider.types";
import { EmailContactInfoTypeEnum, UndeliverableEmailRecordType } from "pages/SharedRecords.types";
import { useEffect, useState } from "react";
import { TextInput, useDataProvider, useTranslate } from "react-admin";
import { useFormContext } from "react-hook-form";

import { EmailReceiverListPropsType, RecipientMode } from "./SendEmailDialog.types";
import UndeliverableEmailAlert from "./UndeliverableEmailsAlert";

export default function EmailReceiverList(props: EmailReceiverListPropsType) {
  const { recipients, useInvoiceContactTypeInitially } = props;
  const translate = useTranslate();
  const [showCcField, setShowCcField] = useState(false);
  const [finalRecipients, setFinalRecipients] = useState(recipients);
  const [undeliverableEmails, setUndeliverableEmails] = useState<UndeliverableEmailRecordType[]>([]);
  const theme = useTheme();
  const { setValue } = useFormContext();
  const [recipientMode, setRecipientMode] = useState<RecipientMode>(
    useInvoiceContactTypeInitially ? "invoice" : "communication",
  );
  const [allRecipientNames, setAllRecipientNames] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const dataProvider = useDataProvider<ExtendedDataProvider>();

  const PREVIEW_COUNT = 5;
  const visibleNames = recipientsExpanded ? allRecipientNames : allRecipientNames.slice(0, PREVIEW_COUNT);
  const hiddenFromDisplayCount = totalCount - allRecipientNames.length;
  const displayText =
    visibleNames.join("; ") +
    (recipientsExpanded && hiddenFromDisplayCount > 0
      ? ` … (${translate("countMore", { count: hiddenFromDisplayCount })})`
      : "");
  const hasMore = totalCount > PREVIEW_COUNT;
  const totalHidden = totalCount - PREVIEW_COUNT;

  useEffect(() => {
    const validateRecipients = async () => {
      try {
        const res = await dataProvider.validateEmailRecipients(finalRecipients);
        setUndeliverableEmails(res.data);
        setAllRecipientNames(res.recipient_names);
        setTotalCount(res.total_count);
      } catch {
        setUndeliverableEmails([]);
      }
    };
    validateRecipients();
  }, [JSON.stringify(finalRecipients)]);

  useEffect(() => {
    if (recipientMode === "invoice" || recipientMode === "communication") {
      setValue("custom_to", undefined);
      const type =
        recipientMode === "invoice" ? EmailContactInfoTypeEnum.INVOICE : EmailContactInfoTypeEnum.COMMUNICATION;

      const updatedRecipients = finalRecipients.map((recipient) => ({
        ...recipient,
        contact_info_type: type,
      }));
      setFinalRecipients(updatedRecipients);
      setValue("recipients", updatedRecipients);
    }
  }, [recipientMode]);

  return (
    <Stack gap={2}>
      {recipientMode !== "custom" && <UndeliverableEmailAlert undeliverableEmails={undeliverableEmails} />}

      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <SimpleMenu
              button={
                <Box display="flex" sx={{ cursor: "pointer" }}>
                  <FormLabel sx={{ mr: 0.5, color: theme.palette.primary.main }}>
                    {translate("resources.emails.fields.to")}&nbsp;
                    {recipientMode === "custom"
                      ? translate("emailing.contact_type.custom")
                      : recipientMode === "invoice"
                        ? translate("emailing.contact_type.invoice")
                        : translate("emailing.contact_type.communication")}
                  </FormLabel>
                  <ArrowDropDown color="primary" />
                </Box>
              }
            >
              {(closeMenu) => (
                <>
                  <MenuItem
                    selected={recipientMode === "communication"}
                    onClick={() => {
                      setRecipientMode("communication");
                      closeMenu();
                    }}
                  >
                    {translate("emailing.use_main_emails", recipients.length)}
                  </MenuItem>
                  <MenuItem
                    selected={recipientMode === "invoice"}
                    onClick={() => {
                      setRecipientMode("invoice");
                      closeMenu();
                    }}
                  >
                    {translate("emailing.use_invoice_emails", recipients.length)}
                  </MenuItem>
                  <MenuItem
                    selected={recipientMode === "custom"}
                    onClick={() => {
                      setRecipientMode("custom");
                      closeMenu();
                    }}
                  >
                    {translate("emailing.use_custom_email")}
                  </MenuItem>
                </>
              )}
            </SimpleMenu>
          </Box>
          {!showCcField && (
            <Link onClick={() => setShowCcField(!showCcField)} sx={{ cursor: "pointer" }}>
              <Box display="flex" alignItems="center">
                Cc
                <Tooltip title={translate("emailing.cc_info")}>
                  <Box component="span" display="flex">
                    <InfoIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </Box>
            </Link>
          )}
        </Box>
        {recipientMode === "custom" && <TextInput source="custom_to" required sx={{ mt: 1 }} />}
        {recipientMode !== "custom" && (
          <TextField
            value={displayText ? displayText : "..."}
            disabled
            sx={{ mb: hasMore ? 0.5 : 3 }}
            multiline={recipientsExpanded}
            fullWidth
          />
        )}
        {recipientMode !== "custom" && hasMore && (
          <Link
            onClick={() => setRecipientsExpanded((prev) => !prev)}
            sx={{ cursor: "pointer", display: "block", mb: 3, fontSize: "0.8rem" }}
          >
            {recipientsExpanded ? translate("actions.show_less") : translate("countMore", { count: totalHidden })}
          </Link>
        )}
        {showCcField && <TextInput source="cc" label="Cc" />}
      </Box>
    </Stack>
  );
}
